import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/dateUtils';

const AccountPayment = () => {
  const toast = useToast();
  const location = useLocation();

  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [partySummary, setPartySummary] = useState(null);

  // Edit state
  const [editingId, setEditingId] = useState(null);

  // Form State
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [transactionType, setTransactionType] = useState('RECEIPT'); // RECEIPT or PAYMENT
  const [amount, setAmount] = useState('');
  const [payMode, setPayMode] = useState('Cash');
  const [payDetail, setPayDetail] = useState('');
  const [remark, setRemark] = useState('');

  // History & Filter State
  const [payments, setPayments] = useState([]);
  const [typeFilter, setTypeFilter] = useState('ALL'); // ALL, RECEIPT, PAYMENT
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Check URL pathname to default Payment In vs Payment Out
  useEffect(() => {
    if (location.pathname.includes('/payments/in')) {
      setTransactionType('RECEIPT');
      setTypeFilter('RECEIPT');
    } else if (location.pathname.includes('/payments/out')) {
      setTransactionType('PAYMENT');
      setTypeFilter('PAYMENT');
    } else {
      setTypeFilter('ALL');
    }
  }, [location.pathname]);

  const fetchAccounts = async () => {
    try {
      const res = await axiosClient.get('/accounts');
      if (res.success) {
        setAccounts(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const fetchPayments = async () => {
    setLoadingHistory(true);
    try {
      let url = `/payments?accountId=${selectedAccountId}`;
      if (typeFilter !== 'ALL') {
        url += `&type=${typeFilter}`;
      }
      const res = await axiosClient.get(url);
      if (res.success) {
        setPayments(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching payments:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, typeFilter]);

  useEffect(() => {
    if (selectedAccountId) {
      const loadSummary = async () => {
        try {
          const res = await axiosClient.get(`/accounts/${selectedAccountId}`);
          if (res.success) {
            setPartySummary(res.data.financialSummary || null);
          }
        } catch (e) {
          console.error(e);
        }
      };
      loadSummary();
    } else {
      setPartySummary(null);
    }
  }, [selectedAccountId]);

  const handleResetForm = () => {
    setEditingId(null);
    setAmount('');
    setRemark('');
    setPayDetail('');
    setPayDate(new Date().toISOString().slice(0, 10));
    setError('');
    setMessage('');
  };

  const handleEdit = (p) => {
    setEditingId(p.Id);
    setSelectedAccountId(p.AccountId || '');
    setPayDate(p.PayDate ? String(p.PayDate).slice(0, 10) : new Date().toISOString().slice(0, 10));
    setTransactionType(p.TransactionType || 'RECEIPT');
    setAmount(p.PaidAmt || p.TotalAmt || '');
    setPayMode(p.paymode || 'Cash');
    setPayDetail(p.payDetail || '');
    setRemark(p.Remark || '');
    setError('');
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAccountId) {
      setError('Please select an Account/Party.');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount.');
      return;
    }

    setError('');
    setMessage('');
    setSaving(true);

    try {
      const payload = {
        accountId: selectedAccountId,
        payDate,
        transactionType,
        amount: parseFloat(amount),
        payMode,
        payDetail,
        remark
      };

      if (editingId) {
        await axiosClient.put(`/payments/${editingId}`, payload);
        const successTxt = `${transactionType === 'RECEIPT' ? 'Receipt' : 'Payment'} transaction updated successfully.`;
        setMessage(successTxt);
        toast.success(successTxt);
      } else {
        await axiosClient.post('/payments', payload);
        const successTxt = `${transactionType === 'RECEIPT' ? 'Receipt' : 'Payment'} of ₹${parseFloat(amount).toFixed(2)} recorded successfully.`;
        setMessage(successTxt);
        toast.success(successTxt);
      }

      handleResetForm();

      // Refresh summary & table
      if (selectedAccountId) {
        const res = await axiosClient.get(`/accounts/${selectedAccountId}`);
        if (res.success) setPartySummary(res.data.financialSummary || null);
      }
      fetchPayments();
    } catch (err) {
      const errMsg = err.message || 'Error saving transaction.';
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this payment record?')) {
      try {
        await axiosClient.delete(`/payments/${id}`);
        toast.success('Payment record deleted successfully.');
        if (editingId === id) handleResetForm();
        fetchPayments();
        if (selectedAccountId) {
          const res = await axiosClient.get(`/accounts/${selectedAccountId}`);
          if (res.success) setPartySummary(res.data.financialSummary || null);
        }
      } catch (err) {
        toast.error(err.message || 'Error deleting payment.');
      }
    }
  };

  const getPageTitle = () => {
    if (location.pathname.includes('/payments/in')) {
      return { title: 'Payment In (Receipts)', desc: 'Record collections and money received from customers' };
    }
    if (location.pathname.includes('/payments/out')) {
      return { title: 'Payment Out (Payments)', desc: 'Record supplier payments and money paid out' };
    }
    return { title: 'Account Payments & Receipts', desc: 'Manage collections and disbursements with instant ledger updates' };
  };

  const pageInfo = getPageTitle();

  return (
    <div className="container-fluid p-0">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="fw-bold mb-0 text-dark">
            <i className="bi bi-cash-coin text-primary me-2"></i> {pageInfo.title}
          </h3>
          <p className="text-muted small mb-0">{pageInfo.desc}</p>
        </div>
      </div>

      <div className="row g-4">
        {/* Entry Form & Party Summary */}
        <div className="col-12 col-lg-5">
          {/* Party Selector & Balance Card */}
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-header bg-light py-2 fw-semibold">
              <i className="bi bi-person-bounding-box text-primary me-2"></i> 1. Select Party / Account
            </div>
            <div className="card-body p-3">
              <label className="form-label small fw-semibold text-muted">Select Customer / Supplier *</label>
              <select
                className="form-select fw-bold mb-3"
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
              >
                <option value="">-- Choose Party / Account --</option>
                {accounts.map((a) => (
                  <option key={a.Id} value={a.Id}>
                    {a.AccountName} ({a.AccountType})
                  </option>
                ))}
              </select>

              {partySummary && (
                <div className="p-3 bg-light rounded border">
                  <div className="row g-2 text-center small mb-2">
                    <div className="col-6">
                      <div className="text-muted">Total Sales</div>
                      <div className="fw-bold text-dark">₹{parseFloat(partySummary.totalSales || 0).toLocaleString('en-IN')}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Total Purchases</div>
                      <div className="fw-bold text-dark">₹{parseFloat(partySummary.totalPurchases || 0).toLocaleString('en-IN')}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Receipts Collected</div>
                      <div className="fw-bold text-success">₹{parseFloat(partySummary.totalReceipts || 0).toLocaleString('en-IN')}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted">Payments Made</div>
                      <div className="fw-bold text-danger">₹{parseFloat(partySummary.totalPayments || 0).toLocaleString('en-IN')}</div>
                    </div>
                  </div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between align-items-center">
                    <strong className="text-muted">Net Party Balance:</strong>
                    <span className={`fs-5 fw-bold ${partySummary.netBalance > 0 ? 'text-danger' : 'text-success'}`}>
                      {partySummary.netBalance > 0 ? `Receivable: ₹${parseFloat(partySummary.netBalance).toLocaleString('en-IN')}` : `Payable: ₹${Math.abs(partySummary.netBalance).toLocaleString('en-IN')}`}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Payment Form */}
          <div className="card shadow-sm border-0">
            <div className="card-header bg-light py-2 fw-semibold d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-wallet2 text-primary me-2"></i> 2. {editingId ? 'Edit Transaction' : 'Record Transaction'}
              </span>
              {editingId && (
                <button type="button" className="btn btn-sm btn-outline-secondary py-0" onClick={handleResetForm}>
                  <i className="bi bi-x-circle me-1"></i> Cancel Edit
                </button>
              )}
            </div>
            <div className="card-body p-3">
              {message && <div className="alert alert-success py-2 small">{message}</div>}
              {error && <div className="alert alert-danger py-2 small">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label small fw-semibold text-muted">Transaction Type *</label>
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn ${transactionType === 'RECEIPT' ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => setTransactionType('RECEIPT')}
                    >
                      <i className="bi bi-arrow-down-left-circle me-1"></i> Receipt (Money In)
                    </button>
                    <button
                      type="button"
                      className={`btn ${transactionType === 'PAYMENT' ? 'btn-danger' : 'btn-outline-danger'}`}
                      onClick={() => setTransactionType('PAYMENT')}
                    >
                      <i className="bi bi-arrow-up-right-circle me-1"></i> Payment (Money Out)
                    </button>
                  </div>
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small fw-semibold text-muted">Payment Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={payDate}
                      onChange={(e) => setPayDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-semibold text-muted">Amount (₹) *</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control fw-bold text-primary"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small fw-semibold text-muted">Payment Mode</label>
                    <select
                      className="form-select"
                      value={payMode}
                      onChange={(e) => setPayMode(e.target.value)}
                    >
                      <option value="Cash">Cash</option>
                      <option value="UPI / GPay / PhonePe">UPI / Online</option>
                      <option value="Bank Transfer (NEFT/RTGS)">Bank Transfer</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-semibold text-muted">Ref / Cheque #</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Txn ID / Chq No"
                      value={payDetail}
                      onChange={(e) => setPayDetail(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-semibold text-muted">Remark / Notes</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Part payment for week sales..."
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                  />
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-ocean flex-grow-1 py-2 d-flex align-items-center justify-content-center gap-2"
                    disabled={saving || !selectedAccountId}
                  >
                    {saving ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-check-circle"></i>}
                    {editingId ? 'Update Transaction' : `Record ${transactionType === 'RECEIPT' ? 'Receipt' : 'Payment'}`}
                  </button>
                  {editingId && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary py-2"
                      onClick={handleResetForm}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Transaction History Table */}
        <div className="col-12 col-lg-7">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="fw-bold mb-0 text-dark">
                <i className="bi bi-clock-history text-primary me-2"></i> Recent History
              </h5>

              {/* Filter Buttons */}
              <div className="btn-group btn-group-sm">
                <button
                  type="button"
                  className={`btn ${typeFilter === 'ALL' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setTypeFilter('ALL')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`btn ${typeFilter === 'RECEIPT' ? 'btn-success' : 'btn-outline-success'}`}
                  onClick={() => setTypeFilter('RECEIPT')}
                >
                  Payment In (Receipts)
                </button>
                <button
                  type="button"
                  className={`btn ${typeFilter === 'PAYMENT' ? 'btn-danger' : 'btn-outline-danger'}`}
                  onClick={() => setTypeFilter('PAYMENT')}
                >
                  Payment Out (Payments)
                </button>
              </div>
            </div>

            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light small">
                    <tr>
                      <th>Date</th>
                      <th>Account Name</th>
                      <th>Type</th>
                      <th>Mode</th>
                      <th className="text-end">Amount (₹)</th>
                      <th>Remark</th>
                      <th className="text-center" style={{ width: '90px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingHistory ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <div className="spinner-border text-primary" role="status"></div>
                        </td>
                      </tr>
                    ) : payments.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5 text-muted small">
                          No transactions found.
                        </td>
                      </tr>
                    ) : (
                      payments.map((p) => (
                        <tr key={p.Id} className={`small ${editingId === p.Id ? 'table-warning' : ''}`}>
                          <td>{formatDate(p.PayDate)}</td>
                          <td className="fw-bold text-dark">{p.AccountName}</td>
                          <td>
                            <span
                              className={`badge ${
                                p.TransactionType === 'RECEIPT'
                                  ? 'bg-success'
                                  : p.TransactionType === 'PAYMENT'
                                  ? 'bg-danger'
                                  : p.TransactionType === 'SALE'
                                  ? 'bg-primary'
                                  : 'bg-warning text-dark'
                              }`}
                            >
                              {p.TransactionType}
                            </span>
                          </td>
                          <td>{p.paymode || 'Cash'}</td>
                          <td className="text-end fw-bold">
                            ₹{parseFloat(p.PaidAmt || p.TotalAmt || 0).toFixed(2)}
                          </td>
                          <td className="text-muted small">{p.Remark || '-'}</td>
                          <td className="text-center">
                            {p.TransactionType !== 'SALE' && p.TransactionType !== 'PURCHASE' ? (
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-outline-primary py-0 px-2"
                                  onClick={() => handleEdit(p)}
                                  title="Edit payment"
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                                <button
                                  className="btn btn-outline-danger py-0 px-2"
                                  onClick={() => handleDelete(p.Id)}
                                  title="Delete payment"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            ) : (
                              <span className="text-muted small">-</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPayment;
